import React, { useState, useEffect, useMemo } from 'react';
import EditIcon from './assets/images/edit-icon.svg';
import DeleteIcon from './assets/images/delete-icon.svg';
import PlusIcon from './assets/images/plus-icon.svg';
import DownloadIcon from './assets/images/download_icon.svg';
import UrunDuzenleModal from './UrunDuzenleModal';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// API URL'i tanımlama
const baseURL = process.env.REACT_APP_API_URL;

function UrunYonetimi() {
    // --- BÖLÜM 1: STATE TANIMLAMALARI (TÜM HAFIZA) ---
    // Tüm state'ler component'in en başında, doğru sırada tanımlandı.
    const [tumUrunler, setTumUrunler] = useState([]);
    const [gosterilenUrunler, setGosterilenUrunler] = useState([]);
    const [loading, setLoading] = useState(true);
    const [seciliKategori, setSeciliKategori] = useState('All');
    
    // Modal Yönetimi
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [seciliUrun, setSeciliUrun] = useState(null);
    const [modalMode, setModalMode] = useState('add'); // 'add' veya 'edit'
    const [aramaTerimi, setAramaTerimi] = useState('');
    const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
    const [pdfYukleniyor, setPdfYukleniyor] = useState(false);
    const [seciliUrunIdleri, setSeciliUrunIdleri] = useState([]);

    // Hızlı Stok Düzenleme
    const [editingStokId, setEditingStokId] = useState(null);
    const [stokInputValue, setStokInputValue] = useState('');

    // Açılır detay bandı
    const [acikUrunId, setAcikUrunId] = useState(null);
    const [urunDetaylari, setUrunDetaylari] = useState({});
    const [detayYukleniyorId, setDetayYukleniyorId] = useState(null);
    const [urunPeriyotlari, setUrunPeriyotlari] = useState({});
    const [urunAraliklar, setUrunAraliklar] = useState({});

    const kaynakEtiketleri = {
        W: 'WhatsApp',
        S: 'Web Sitesi',
        R: 'Resmi Evrak',
        Bilinmiyor: 'Bilinmiyor'
    };

    const kaynakRenkleri = {
        W: '#25D366',
        S: '#3783c2',
        R: '#f4bf4a'
    };

    // --- BÖLÜM 2: YARDIMCI DEĞİŞKENLER (useMemo) ---
    // Kategoriler, sadece tumUrunler değiştiğinde yeniden hesaplanır.
    const kategoriler = useMemo(() => {
        const kategoriSeti = new Set(tumUrunler.map(u => u.kategori).filter(Boolean));
        const tumKategoriler = ['All', ...Array.from(kategoriSeti)];
        
        // Kullanıcı rolünü kontrol et
        const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;
        const isAdmin = user?.rol === 'admin';

        if (isAdmin) {
            return tumKategoriler;
        } else {
            // Admin olmayan kullanıcılar için izin verilen kategoriler
            const izinliKategoriler = ['All', 'Yan Ürünler', 'Esmaül Hüsna', 'Metrelik Ebat', 'Büyük Ebat'];
            return tumKategoriler.filter(kategori => izinliKategoriler.includes(kategori));
        }
    }, [tumUrunler]);

    // --- BÖLÜM 3: FONKSİYONLAR ---

    // Backend'den tüm ürünleri çeken ana fonksiyon
    const fetchUrunler = () => {
        setLoading(true);
        fetch(`api/urunler`)
            .then(response => response.json())
            .then(data => {
                setTumUrunler(data);
                setGosterilenUrunler(data);
                setLoading(false);
            })
            .catch(error => {
                console.error('Ürünleri çekerken hata:', error);
                setLoading(false);
            });
    };

    // Hızlı Stok Düzenleme Fonksiyonları
    const handleStokDuzenleClick = (urun) => {
        setEditingStokId(urun.id);
        setStokInputValue(urun.stok_miktari);
    };

    const getRowStyle = (urun) => {
        const { stok_miktari, kritik_esik } = urun;

        if (stok_miktari <= 0) {
            return { backgroundColor: '#f8d7da', color: '#721c24' }; // Kırmızı (Stok Bitti)
        }
        if (stok_miktari <= kritik_esik * 0.5) {
            return { backgroundColor: '#fff3cd', color: '#856404' }; // Turuncu (Kritik Eşiğin Yarısı)
        }
        if (stok_miktari <= kritik_esik) {
            return { backgroundColor: '#ffffcc', color: '#333' }; // Sarı (Kritik Eşikte)
        }
        // Varsayılan stil
        return { backgroundColor: '#ffffff' }; 
    };

    const handleStokKaydet = (urunId) => {
        const urun = tumUrunler.find(u => u.id === urunId);
        const yeniStok = parseInt(stokInputValue) || 0;
        const token = localStorage.getItem('token');
        const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;

        const headers = {
            'Content-Type': 'application/json'
        };

        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        fetch(`api/urunler/${urunId}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({
                ...urun,
                stok_miktari: yeniStok,
                stok_guncelleyen_kullanici_adi: user?.gorunen_ad || user?.ad || user?.kullanici_adi || 'Bilinmiyor'
            })
        })
        .then(res => res.json())
        .then((responseData) => {
            // Tüm listeyi backend'den tekrar çekmek yerine (fetchUrunler,
            // setLoading(true) çağırdığı için sayfanın tamamen yeniden
            // render olmasına/"yenilenmesine" sebep oluyordu) sadece
            // ilgili ürünü local state'te güncelliyoruz.
            setTumUrunler(prevUrunler =>
                prevUrunler.map(u =>
                    u.id === urunId ? { ...u, stok_miktari: yeniStok } : u
                )
            );

            if (responseData?.stok_hareketi) {
                setUrunDetaylari(prev => {
                    const mevcutDetay = prev[urunId];
                    if (!mevcutDetay) {
                        return prev;
                    }

                    return {
                        ...prev,
                        [urunId]: {
                            ...mevcutDetay,
                            stok_arsivi: [responseData.stok_hareketi, ...(mevcutDetay.stok_arsivi || [])]
                        }
                    };
                });
            }
        })
        .catch(error => console.error('Stok güncellenirken hata:', error))
        .finally(() => setEditingStokId(null));
    };

    const handleUrunSatiriClick = (urunId) => {
        const yeniAcikId = acikUrunId === urunId ? null : urunId;
        setAcikUrunId(yeniAcikId);

        if (yeniAcikId && !urunPeriyotlari[yeniAcikId]) {
            setUrunPeriyotlari(prev => ({ ...prev, [yeniAcikId]: 'bugun' }));
        }

        if (yeniAcikId && !urunDetaylari[yeniAcikId]) {
            setDetayYukleniyorId(yeniAcikId);
            fetch(`api/urunler/${yeniAcikId}/detay-panel`)
                .then(res => res.json())
                .then(data => {
                    setUrunDetaylari(prev => ({ ...prev, [yeniAcikId]: data }));
                })
                .catch(error => {
                    console.error('Ürün detay panel verisi alınamadı:', error);
                })
                .finally(() => setDetayYukleniyorId(null));
        }
    };

    const handlePeriyotDegistir = (urunId, periyot) => {
        setUrunPeriyotlari(prev => ({ ...prev, [urunId]: periyot }));
        if (periyot === 'aralik' && !urunAraliklar[urunId]) {
            const today = new Date().toISOString().split('T')[0];
            const aralik = { baslangic: today, bitis: today };
            setUrunAraliklar(prev => ({ ...prev, [urunId]: aralik }));
            fetchAralikData(urunId, today, today);
        }
    };

    const fetchAralikData = (urunId, baslangic, bitis) => {
        if (!baslangic || !bitis) return;
        fetch(`api/urunler/${urunId}/satis-aralik?start_date=${baslangic}&end_date=${bitis}`)
            .then(res => res.json())
            .then(data => {
                setUrunDetaylari(prev => ({
                    ...prev,
                    [urunId]: {
                        ...prev[urunId],
                        satis_verileri: {
                            ...(prev[urunId]?.satis_verileri || {}),
                            aralik: data
                        }
                    }
                }));
            })
            .catch(error => console.error('Aralık verisi alınamadı:', error));
    };

    const getAktifPeriyotData = (urunId) => {
        const periyot = urunPeriyotlari[urunId] || 'bugun';
        const detay = urunDetaylari[urunId];
        const satisVerileri = detay?.satis_verileri || {};
        return satisVerileri[periyot] || { toplam_siparis: 0, kaynaklar: {} };
    };

    const formatStokFarki = (fark) => {
        if (fark > 0) return `+${fark}`;
        return `${fark}`;
    };

    const formatTutar = (tutar) => {
        return Number(tutar || 0).toLocaleString('tr-TR', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        });
    };

    const handleUrunSecim = (urunId) => {
        setSeciliUrunIdleri(prev =>
            prev.includes(urunId)
                ? prev.filter(id => id !== urunId)
                : [...prev, urunId]
        );
    };

    const handleTumunuSecUrun = (e) => {
        if (e.target.checked) {
            setSeciliUrunIdleri(gosterilenUrunler.map(u => u.id));
        } else {
            setSeciliUrunIdleri([]);
        }
    };

    const normalizeTr = (str) => (str || '')
        .replace(/ş/g,'s').replace(/Ş/g,'S')
        .replace(/ı/g,'i').replace(/İ/g,'I')
        .replace(/ğ/g,'g').replace(/Ğ/g,'G')
        .replace(/ü/g,'u').replace(/Ü/g,'U')
        .replace(/ö/g,'o').replace(/Ö/g,'O')
        .replace(/ç/g,'c').replace(/Ç/g,'C');

    const handlePdfIndir = async (siralamaKriterleri) => {
        setPdfYukleniyor(true);
        try {
            const kaynakUrunler = seciliUrunIdleri.length > 0
                ? gosterilenUrunler.filter(u => seciliUrunIdleri.includes(u.id))
                : gosterilenUrunler;
            const urunlerVeri = await Promise.all(
                kaynakUrunler.map(urun =>
                    fetch(`api/urunler/${urun.id}/detay-panel`)
                        .then(r => r.json())
                        .then(data => ({ ...urun, buYilSatis: data.satis_verileri?.yil?.toplam_siparis || 0 }))
                        .catch(() => ({ ...urun, buYilSatis: 0 }))
                )
            );
            const sirali = [...urunlerVeri].sort((a, b) =>
                siralamaKriterleri === 'stok'
                    ? b.stok_miktari - a.stok_miktari
                    : b.buYilSatis - a.buYilSatis
            );
            const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const kategoriAdi = seciliKategori === 'All' ? 'Tum Kategoriler' : normalizeTr(seciliKategori);
            const siralamaAdi = siralamaKriterleri === 'stok' ? 'Stok Adeti' : 'Bu Yil Satis Adeti';
            const tarihStr = new Date().toLocaleDateString('tr-TR');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(14);
            doc.text(`Urun Listesi - ${kategoriAdi}`, 14, 15);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.text(`Siralama: ${siralamaAdi} (Coktan Az'a) | Tarih: ${tarihStr}`, 14, 22);
            autoTable(doc, {
                startY: 28,
                head: [['#', 'Urun Adi', 'Kategori', 'Bu Yil (Satis Adeti)', 'Stok']],
                body: sirali.map((urun, index) => [
                    index + 1,
                    normalizeTr(urun.urun_adi),
                    normalizeTr(urun.kategori || '-'),
                    urun.buYilSatis,
                    urun.stok_miktari
                ]),
                styles: { font: 'helvetica', fontSize: 9, cellPadding: 3 },
                headStyles: { fillColor: [56, 132, 194], textColor: 255, fontStyle: 'bold' },
                columnStyles: {
                    0: { cellWidth: 14, halign: 'center' },
                    1: { cellWidth: 'auto', halign: 'left' },
                    2: { cellWidth: 38, halign: 'left' },
                    3: { cellWidth: 44, halign: 'left' },
                    4: { cellWidth: 20, halign: 'center' },
                },
                alternateRowStyles: { fillColor: [248, 248, 248] },
                margin: { left: 14, right: 14 },
            });
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setFont('helvetica', 'normal');
                doc.text(`Sayfa ${i} / ${pageCount}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
            }
            doc.save(`urun-listesi-${kategoriAdi}-${tarihStr.replace(/\./g, '-')}.pdf`);
        } catch (error) {
            console.error('PDF olusturulurken hata:', error);
            alert('PDF olusturulurken bir hata olustu.');
        } finally {
            setPdfYukleniyor(false);
        }
    };

    // Modal Yönetim Fonksiyonları
    const handleDuzenleClick = (urun) => {
        setModalMode('edit');
        setSeciliUrun(urun);
        setIsModalOpen(true);
    };

    const handleYeniUrunClick = () => {
        setModalMode('add');
        setSeciliUrun(null);
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSeciliUrun(null);
    };

    const handleTedarikListesineEkle = async (urunId) => {
        // localStorage'dan kullanıcı bilgilerini al
        const userStr = localStorage.getItem('user');
        if (!userStr) {
            alert('Kullanıcı oturumu bulunamadı!');
            return;
        }
        
        const user = JSON.parse(userStr);
        const mevcutKullaniciId = user.id;
        const mevcutKullaniciAdi = user.ad;

        try {
            const response = await fetch(`api/tedarik-listesi`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    urun_id: urunId, 
                    ekleyen_kullanici_id: mevcutKullaniciId,
                    ekleyen_kullanici_adi: mevcutKullaniciAdi
                })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.hata);
            
            alert(data.mesaj);
        } catch (error) {
            alert(`Hata: ${error.message}`);
        }
    };

    const handleSaveUrun = (urunData, file) => {
        // Adım 1: Eğer yeni bir dosya seçilmişse, önce onu yükle.
        if (file) {
            // Dosya boyutu kontrolü (16MB = 16 * 1024 * 1024 bytes)
            const maxSize = 16 * 1024 * 1024; // 16MB
            if (file.size > maxSize) {
                alert('Dosya çok büyük! Maksimum 16MB boyutunda dosya yükleyebilirsiniz.');
                return;
            }
            
            // Dosya tipi kontrolü
            const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/gif', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                alert('Geçersiz dosya tipi! PNG, JPG, JPEG, GIF veya WEBP dosyası seçin.');
                return;
            }

            const formData = new FormData();
            formData.append('file', file);

            fetch(`api/urunler/fotograf-yukle`, {
                method: 'POST',
                body: formData,
            })
            .then(res => res.json())
            .then(uploadData => {
                if (uploadData.hata) {
                    throw new Error(uploadData.hata);
                }
                // Yükleme başarılıysa, ürün verisine yeni fotoğraf yolunu ekle
                // ve Adım 2'ye geç.
                const guncelUrunData = { ...urunData, fotograf_yolu: uploadData.dosya_yolu };
                kaydetVeyaGuncelle(guncelUrunData);
            })
            .catch(error => {
                console.error('Fotoğraf yüklenirken hata:', error);
                alert(`Fotoğraf yüklenemedi: ${error.message}`);
            });
        } else {
            // Eğer yeni dosya seçilmemişse, doğrudan mevcut verilerle kaydet.
            kaydetVeyaGuncelle(urunData);
        }
    };

    const kaydetVeyaGuncelle = (urunData) => {
        let endpoint = `api/urunler`;
        let method = 'POST';

        if (modalMode === 'edit') {
            endpoint = `api/urunler/${urunData.id}`;
            method = 'PUT';
        }

        fetch(endpoint, {
            method: method,
            headers: { 
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(urunData)
        })
        .then(res => res.json())
        .then(data => {
            if (data.hata) {
                throw new Error(data.hata);
            }
            alert(data.mesaj);
            handleModalClose(); // Formu kapat
            fetchUrunler();     // Listeyi yenile
        })
        .catch(error => {
            console.error('Ürün kaydedilirken hata:', error);
            alert(`Ürün kaydedilemedi: ${error.message}`);
        });
    };

    const handleUrunSil = (urunId) => {
    // Kullanıcıdan onay alarak yanlışlıkla silmeyi önleyelim
        if (window.confirm(`Emin misiniz? ID'si ${urunId} olan ürün kalıcı olarak silinecektir.`)) {
            
            // Backend'e DELETE isteği gönder
            fetch(`api/urunler/${urunId}`, {
                method: 'DELETE'
            })
            .then(res => {
                if (!res.ok) {
                    // Sunucudan bir hata mesajı gelirse onu göster
                    return res.json().then(err => { throw new Error(err.hata || 'Silme işlemi başarısız oldu.') });
                }
                return res.json();
            })
            .then(data => {
                alert(data.mesaj); // Başarı mesajını göster
                fetchUrunler(); // Listeyi en güncel haliyle yeniden çek
            })
            .catch(error => {
                console.error('Ürün silinirken hata:', error);
                alert(error.message);
            });
        }
    };

        // --- BÖLÜM 4: EFFECT'LER ---

    // Component ilk yüklendiğinde ürünleri bir kez çeker
    useEffect(() => {
        fetchUrunler();
    }, []);

    // Seçili kategori ve arama terimi değiştiğinde listeyi filtreler
    useEffect(() => {
        let filtrelenmisUrunler = tumUrunler;
        
        // Kullanıcı rolünü kontrol et
        const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;
        const isAdmin = user?.rol === 'admin';
        
        // Admin olmayan kullanıcılar için izin verilen kategoriler
        if (!isAdmin) {
            const izinliKategoriler = ['Yan Ürünler', 'Esmaül Hüsna', 'Metrelik Ebat', 'Büyük Ebat'];
            filtrelenmisUrunler = filtrelenmisUrunler.filter(urun => 
                izinliKategoriler.includes(urun.kategori)
            );
        }
        
        // Sonra kategoriye göre filtrele
        if (seciliKategori !== 'All') {
            filtrelenmisUrunler = filtrelenmisUrunler.filter(urun => urun.kategori === seciliKategori);
        }
        
        // Son olarak arama terimine göre filtrele
        if (aramaTerimi) {
            const searchLower = aramaTerimi.toLowerCase();
            filtrelenmisUrunler = filtrelenmisUrunler.filter(urun => 
                urun.urun_adi.toLowerCase().includes(searchLower) ||
                urun.urun_kodu.toLowerCase().includes(searchLower) ||
                (urun.kategori && urun.kategori.toLowerCase().includes(searchLower))
            );
        }
        
        setSeciliUrunIdleri([]);
        setGosterilenUrunler(filtrelenmisUrunler);
    }, [seciliKategori, tumUrunler, aramaTerimi]);


    // --- BÖLÜM 5: RENDER ---

    if (loading) {
        return <div style={{ padding: '20px' }}>Ürünler yükleniyor...</div>;
    }

    return (
        <div style={{ background: '#F8F8F8', padding: '20px', borderRadius: '8px', height: 'calc(100vh - 110px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexShrink: 0 }}>
                <style>{`
                .siparis-satiri {
                    transition: all 0.2s ease-in-out;
                }
                .siparis-satiri:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
                    z-index: 10;
                    position: relative;
                }
                `}</style>
                <div style={{display: 'flex', alignItems: 'center', gap: '20px'}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                        <label style={{fontWeight: '500'}}>Kategori Seç:</label>
                        <select
                            value={seciliKategori}
                            onChange={(e) => setSeciliKategori(e.target.value)}
                            style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', minWidth: '200px' }}
                        >
                            {kategoriler.map(kategori => (
                                <option key={kategori} value={kategori}>
                                    {kategori === 'All' ? 'Tüm Kategoriler' : kategori}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div style={{position: 'relative'}}>
                        <input
                            type="text"
                            value={aramaTerimi}
                            onChange={(e) => setAramaTerimi(e.target.value)}
                            placeholder="Ürün Ara..."
                            style={{
                                padding: '8px 10px',
                                borderRadius: '8px',
                                border: '1px solid #ccc', 
                                width: '300px',
                                fontSize: '14px',
                                backgroundColor: 'white',
                            }}
                        />
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                        onClick={() => setIsPdfModalOpen(true)}
                        disabled={pdfYukleniyor}
                        style={{ background: '#F8F8F8', boxShadow: '2px 2px 2px rgba(0, 0, 0, 0.10)', borderRadius: '10px', border: '0.50px black solid', width: '129px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: pdfYukleniyor ? 'not-allowed' : 'pointer', fontWeight: '500', opacity: pdfYukleniyor ? 0.6 : 1 }}
                    >
                        <img src={DownloadIcon} alt="PDF" style={{ height: '14px' }} />
                        <span>{pdfYukleniyor ? 'Yükleniyor...' : 'PDF İndir'}</span>
                    </button>
                    {localStorage.getItem('user') && JSON.parse(localStorage.getItem('user')).rol === 'admin' && (
                        <button
                            onClick={handleYeniUrunClick}
                            style={{ background: '#F8F8F8', boxShadow: '2px 2px 2px rgba(0, 0, 0, 0.10)', borderRadius: '10px', border: '0.50px black solid', width: '129px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', fontWeight: '500' }}
                        >
                            <img src={PlusIcon} alt="Yeni" style={{ height: '14px' }} />
                            <span>Yeni Ürün Ekle</span>
                        </button>
                    )}
                </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', border: '1px solid #eee', borderRadius: '4px' }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '14px' }}>
                <thead>
                    <tr style={{ background: '#E5E5E5', height: '35px' }}>
                        <th style={{ width: '30px', padding: '11px', textAlign: 'center', position: 'sticky', top: 0, zIndex: 20, background: '#E5E5E5', borderBottom: '1px solid #ddd' }}>
                            <input
                                type="checkbox"
                                onChange={handleTumunuSecUrun}
                                checked={gosterilenUrunler.length > 0 && seciliUrunIdleri.length === gosterilenUrunler.length}
                            />
                        </th>
                        <th style={{ width: '4%', padding: '12px', textAlign: 'center', fontWeight: '500', position: 'sticky', top: 0, zIndex: 20, background: '#E5E5E5', borderBottom: '1px solid #ddd' }}></th>
                        <th style={{ width: '8%', padding: '12px', textAlign: 'left', fontWeight: '500', position: 'sticky', top: 0, zIndex: 20, background: '#E5E5E5', borderBottom: '1px solid #ddd' }}>Ürün Kodu</th>
                        <th style={{ width: '20%', padding: '12px', textAlign: 'left', fontWeight: '500', position: 'sticky', top: 0, zIndex: 20, background: '#E5E5E5', borderBottom: '1px solid #ddd' }}>Ürün Adı</th>
                        <th style={{ width: '12%', padding: '12px', textAlign: 'left', fontWeight: '500', position: 'sticky', top: 0, zIndex: 20, background: '#E5E5E5', borderBottom: '1px solid #ddd' }}>Kategori</th>
                        <th style={{ width: '10%', padding: '12px', textAlign: 'center', fontWeight: '500', position: 'sticky', top: 0, zIndex: 20, background: '#E5E5E5', borderBottom: '1px solid #ddd' }}>Fiyat</th>
                        <th style={{ width: '8%', padding: '12px', textAlign: 'center', fontWeight: '500', position: 'sticky', top: 0, zIndex: 20, background: '#E5E5E5', borderBottom: '1px solid #ddd' }}>Stok</th>
                        <th style={{ width: '10%', padding: '12px', textAlign: 'center', fontWeight: '500', position: 'sticky', top: 0, zIndex: 20, background: '#E5E5E5', borderBottom: '1px solid #ddd' }}>Paraşüt ID</th>
                        <th style={{ width: '10%', padding: '12px', textAlign: 'center', fontWeight: '500', position: 'sticky', top: 0, zIndex: 20, background: '#E5E5E5', borderBottom: '1px solid #ddd' }}>Wix ID</th>
                        <th style={{ width: '18%', padding: '12px', textAlign: 'center', fontWeight: '500', position: 'sticky', top: 0, zIndex: 20, background: '#E5E5E5', borderBottom: '1px solid #ddd' }}>İşlemler</th>
                    </tr>
                </thead>
                <tbody>
                    {gosterilenUrunler.map((urun) => {
                        const aktifPeriyot = urunPeriyotlari[urun.id] || 'bugun';
                        const aktifPeriyotData = getAktifPeriyotData(urun.id);
                        const stokArsivi = urunDetaylari[urun.id]?.stok_arsivi || [];

                        return (
                            <React.Fragment key={urun.id}>
                                <tr
                                    className="siparis-satiri"
                                    style={{ ...getRowStyle(urun), borderBottom: '1px solid #eee', cursor: 'pointer' }}
                                    onClick={() => handleUrunSatiriClick(urun.id)}
                                >
                                    <td style={{ padding: '8px', textAlign: 'center', width: '30px' }} onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={seciliUrunIdleri.includes(urun.id)}
                                            onChange={() => handleUrunSecim(urun.id)}
                                        />
                                    </td>
                                    <td style={{ padding: '8px', textAlign: 'center' }}>
                                        {urun.fotograf_yolu ? (
                                            <img
                                                src={`${baseURL}/api/uploads/${urun.fotograf_yolu}`}
                                                alt={urun.urun_adi}
                                                style={{ width: '40px', height: '40px', borderRadius: '4px', objectFit: 'cover' }}
                                                onLoad={() => console.log('Resim yüklendi:', `${baseURL}/api/uploads/${urun.fotograf_yolu}`)}
                                                onError={(e) => console.log('Resim yüklenemedi:', `${baseURL}/api/uploads/${urun.fotograf_yolu}`, e)}
                                            />
                                        ) : (
                                            <div style={{ width: '40px', height: '40px', borderRadius: '4px', background: '#f0f0f0', display: 'inline-block' }}></div>
                                        )}
                                    </td>
                                    <td style={{ padding: '12px' }}>{urun.urun_kodu}</td>
                                    <td style={{ padding: '12px' }}>{urun.urun_adi}</td>
                                    <td style={{ padding: '12px' }}>{urun.kategori}</td>
                                    <td style={{ padding: '12px', textAlign: 'center' }}>{(urun.fiyat || 0).toFixed(2)} ₺</td>
                                    <td style={{ padding: '12px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                        {editingStokId === urun.id ? (
                                            <input
                                                type="number"
                                                value={stokInputValue}
                                                onChange={(e) => setStokInputValue(e.target.value)}
                                                onBlur={() => handleStokKaydet(urun.id)}
                                                onKeyDown={(e) => { if (e.key === 'Enter') handleStokKaydet(urun.id); }}
                                                autoFocus
                                                style={{ width: '60px', textAlign: 'center', padding: '5px' }}
                                            />
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                                <span>{urun.stok_miktari}</span>
                                                <button onClick={() => handleStokDuzenleClick(urun)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                                                    <img src={EditIcon} alt="Stok Düzenle" style={{ width: '14px', height: '14px' }} />
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'center', fontSize: '12px', color: '#666' }}>
                                        {urun.parasut_id || '-'}
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'center', fontSize: '12px', color: '#666' }}>
                                        {urun.wix_id || '-'}
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                        <button
                                            onClick={() => handleTedarikListesineEkle(urun.id)}
                                            title="Tedarik Listesine Ekle"
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', marginRight: '10px' }}
                                        >
                                            <img src={PlusIcon} alt="Ekle" style={{ width: '16px' }} />
                                        </button>
                                        {localStorage.getItem('user') && JSON.parse(localStorage.getItem('user')).rol === 'admin' && (
                                            <>
                                                <button
                                                    onClick={() => handleDuzenleClick(urun)}
                                                    title="Düzenle"
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', marginRight: '10px' }}
                                                >
                                                    <img src={EditIcon} alt="Düzenle" style={{ width: '16px' }} />
                                                </button>
                                                <button
                                                    onClick={() => handleUrunSil(urun.id)}
                                                    title="Ürün Sil"
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                                >
                                                    <img src={DeleteIcon} alt="Sil" style={{ width: '20px' }} />
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>

                                {acikUrunId === urun.id && (
                                    <tr>
                                        <td colSpan="10" style={{ padding: '12px', background: '#f2f4f7' }}>
                                            {detayYukleniyorId === urun.id ? (
                                                <p style={{ margin: 0, fontFamily: 'Poppins' }}>Detaylar yükleniyor...</p>
                                            ) : (
                                                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontFamily: 'Poppins' }}>
                                    <div style={{ flex: 2, minWidth: '320px', background: 'white', border: '1px solid #e3e3e3', borderRadius: '8px', padding: '14px' }}>

                                                        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
                                                            {[
                                                                { key: 'bugun', label: 'Bugün' },
                                                                { key: 'hafta', label: 'Bu Hafta' },
                                                                { key: 'ay', label: 'Bu Ay' },
                                                                { key: 'yil', label: 'Bu Yıl' },
                                                                { key: 'aralik', label: 'Tarih Aralığı' }
                                                            ].map(item => (
                                                                <button
                                                                    key={item.key}
                                                                    onClick={() => handlePeriyotDegistir(urun.id, item.key)}
                                                                    style={{
                                                                        padding: '6px 12px',
                                                                        borderRadius: '6px',
                                                                        border: '1px solid #ccc',
                                                                        background: aktifPeriyot === item.key ? '#3884c2' : '#f8f8f8',
                                                                        color: aktifPeriyot === item.key ? 'white' : '#333',
                                                                        fontWeight: aktifPeriyot === item.key ? '600' : '500',
                                                                        cursor: 'pointer',
                                                                        fontSize: '12px'
                                                                    }}
                                                                >
                                                                    {item.label}
                                                                </button>
                                                            ))}
                                                            {aktifPeriyot === 'aralik' && (
                                                                <>
                                                                    <input
                                                                        type="date"
                                                                        value={urunAraliklar[urun.id]?.baslangic || ''}
                                                                        onClick={e => e.stopPropagation()}
                                                                        onChange={e => {
                                                                            const baslangic = e.target.value;
                                                                            setUrunAraliklar(prev => ({ ...prev, [urun.id]: { ...prev[urun.id], baslangic } }));
                                                                            fetchAralikData(urun.id, baslangic, urunAraliklar[urun.id]?.bitis || '');
                                                                        }}
                                                                        style={{ padding: '5px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px', fontFamily: 'Poppins' }}
                                                                    />
                                                                    <span style={{ fontSize: '12px', color: '#666' }}>-</span>
                                                                    <input
                                                                        type="date"
                                                                        value={urunAraliklar[urun.id]?.bitis || ''}
                                                                        onClick={e => e.stopPropagation()}
                                                                        onChange={e => {
                                                                            const bitis = e.target.value;
                                                                            setUrunAraliklar(prev => ({ ...prev, [urun.id]: { ...prev[urun.id], bitis } }));
                                                                            fetchAralikData(urun.id, urunAraliklar[urun.id]?.baslangic || '', bitis);
                                                                        }}
                                                                        style={{ padding: '5px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px', fontFamily: 'Poppins' }}
                                                                    />
                                                                </>
                                                            )}
                                                        </div>

                                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(180px, 1fr))', gap: '16px' }}>
                                                            <div style={{ background: '#fcfcfc', border: '1px solid #ececec', borderRadius: '8px', padding: '16px' }}>
                                                                <div style={{ fontSize: '13px', color: '#272935', fontWeight: '600', paddingBottom: '10px', borderBottom: '1px solid #ececec' }}>Toplam Sipariş</div>
                                                                <div style={{ minHeight: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '58px', lineHeight: 1, fontWeight: '700', color: '#272935' }}>
                                                                    {aktifPeriyotData.toplam_siparis || 0}
                                                                </div>
                                                            </div>

                                                            <div style={{ background: '#fcfcfc', border: '1px solid #ececec', borderRadius: '8px', padding: '16px' }}>
                                                                <div style={{ fontSize: '13px', color: '#272935', fontWeight: '600', paddingBottom: '10px', borderBottom: '1px solid #ececec', marginBottom: '10px' }}>Sipariş Kaynağı</div>
                                                                {['W', 'S', 'R'].map((kaynak) => (
                                                                        <div key={kaynak} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: '14px' }}>
                                                                            <span style={{ backgroundColor: kaynakRenkleri[kaynak] || '#6c757d', color: 'white', padding: '4px 12px', borderRadius: '12px', textAlign: 'center', fontWeight: '500' }}>
                                                                                {kaynakEtiketleri[kaynak] || kaynak}
                                                                            </span>
                                                                            <span style={{ fontWeight: '700', color: '#272935' }}>{aktifPeriyotData.kaynaklar?.[kaynak] || 0}</span>
                                                                        </div>
                                                                    ))}
                                                            </div>

                                                            <div style={{ background: '#fcfcfc', border: '1px solid #ececec', borderRadius: '8px', padding: '16px' }}>
                                                                <div style={{ fontSize: '13px', color: '#272935', fontWeight: '600', paddingBottom: '10px', borderBottom: '1px solid #ececec' }}>Toplam Sipariş (₺)</div>
                                                                <div style={{ minHeight: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '42px', lineHeight: 1.1, fontWeight: '700', color: '#000000', textAlign: 'center' }}>
                                                                    {formatTutar(aktifPeriyotData.toplam_tutar)} ₺
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div style={{ flex: 1, minWidth: '320px', background: 'white', border: '1px solid #e3e3e3', borderRadius: '8px', padding: '14px' }}>
                                                        <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', letterSpacing: '0.2px', color: '#555' }}>STOK GÜNCELLEME ARŞİVİ</h4>
                                                        <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid #ececec', borderRadius: '6px' }}>
                                                            {stokArsivi.length > 0 ? (
                                                                stokArsivi.map((hareket) => (
                                                                    <div
                                                                        key={hareket.id}
                                                                        style={{
                                                                            padding: '10px 12px',
                                                                            borderBottom: '1px solid #f1f1f1',
                                                                            fontSize: '12px',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: '10px',
                                                                            whiteSpace: 'nowrap',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis'
                                                                        }}
                                                                    >
                                                                        <span style={{ color: '#777', flexShrink: 0 }}>{hareket.islem_tarihi}</span>
                                                                        <span style={{ color: '#bdbdbd', flexShrink: 0 }}>|</span>
                                                                        <span style={{ fontWeight: '600', color: '#444', flexShrink: 0 }}>{hareket.kullanici}</span>
                                                                        <span style={{ color: '#bdbdbd', flexShrink: 0 }}>|</span>
                                                                        <span style={{ fontWeight: '600', color: '#2f2f2f' }}>
                                                                            {hareket.onceki_stok} {'>'} {hareket.yeni_stok} ({formatStokFarki(hareket.fark)})
                                                                        </span>
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <div style={{ padding: '12px', fontSize: '12px', color: '#777' }}>Henüz stok güncelleme kaydı yok.</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
            </div>
            
            <UrunDuzenleModal 
                isOpen={isModalOpen}
                onClose={handleModalClose}
                urun={seciliUrun}
                kategoriler={kategoriler.filter(k => k !== 'All')} // "All" seçeneğini gönderme
                onSave={handleSaveUrun}
                mode={modalMode}
            />

            {isPdfModalOpen && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: 'white', borderRadius: '12px', padding: '28px', width: '340px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                        <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>PDF Sıralama Seçeneği</h3>
                        <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#666' }}>
                            {seciliKategori === 'All' ? 'Tüm Kategoriler' : seciliKategori} ürünlerini hangi kritere göre sıralamak istersiniz?
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <button
                                onClick={() => { setIsPdfModalOpen(false); handlePdfIndir('satis'); }}
                                style={{ padding: '11px 16px', borderRadius: '8px', border: '1px solid #3884c2', background: '#3884c2', color: 'white', fontWeight: '500', cursor: 'pointer', fontSize: '14px' }}
                            >
                                Bu Yıl Satış Adeti (Çoktan Az'a)
                            </button>
                            <button
                                onClick={() => { setIsPdfModalOpen(false); handlePdfIndir('stok'); }}
                                style={{ padding: '11px 16px', borderRadius: '8px', border: '1px solid #ccc', background: 'white', color: '#333', fontWeight: '500', cursor: 'pointer', fontSize: '14px' }}
                            >
                                Stok Adeti (Çoktan Az'a)
                            </button>
                            <button
                                onClick={() => setIsPdfModalOpen(false)}
                                style={{ padding: '8px', borderRadius: '8px', border: 'none', background: 'none', color: '#888', cursor: 'pointer', fontSize: '13px', marginTop: '4px' }}
                            >
                                İptal
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default UrunYonetimi;